import { Component, Injector, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ClientInvoiceDto, DeliveryNoteDto, InvoiceProductDto, InvoiceServiceProxy, ProductInvoiceDto, StockServiceProxy, WarehouseSelectDto } from '@shared/service-proxies/service-proxies';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { AppComponentBase } from '@shared/app-component-base';

@Component({
  templateUrl: './stock-transfer-menu.component.html',
  styleUrls: ['./stock-transfer-menu.component.scss']
})
export class StockTransferMenuComponent extends AppComponentBase implements OnInit {
  saving = false;
  title: string;

  fgRefer: FormGroup;
  filteredProducts: Observable<ProductInvoiceDto[]>;

  products: InvoiceProductDto[] = [];
  warehouses: WarehouseSelectDto[] = [];
  deliveryNoteDto: DeliveryNoteDto = new DeliveryNoteDto();

  newProduct: FormControl = this.fb.control('');

  constructor(
    injector: Injector,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<StockTransferMenuComponent>,

    public _stockService: StockServiceProxy,
    public _invoiceService: InvoiceServiceProxy,
  ) {
    super(injector);
    this._stockService.getAllWarehouses().subscribe((result) => {
      let hasInternalTransit = abp.setting.getBoolean('AllowInternalTransitStock');
      if (hasInternalTransit) {
        this.warehouses = result.filter(x => !x.isStatic);
      } else {
        this.warehouses = result;
      }
    });
  }

  get productsArr() {
    return this.fgRefer.controls["listProducts"] as FormArray;
  }

  ngOnInit() {
    this.title = 'Transfer';
    this.createReferForm();

    this.filteredProducts = this.fgRefer.get('productInput').valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(val => {
        return this._stockService.getProductsWithVariantInStock(val || '')
      })
    );
  }


  createReferForm(): void {
    this.fgRefer = this.fb.group({
      warehouseFromId: ['', [Validators.required]],
      warehouseToId: ['', [Validators.required]],
      description: ['', [Validators.required, Validators.maxLength(64), Validators.minLength(20)]],
      productInput: [],
      listProducts: this.fb.array([], [Validators.required]),
      userId: [this.appSession.user.id],
      salePointId: [this.appSession.user.salePointConfigId]
    }, { validators: [this.differentWarehousesValidator] });
  }

  // Origen y destino no pueden ser el mismo depósito.
  differentWarehousesValidator = (fg: FormGroup) => {
    const from = fg.get('warehouseFromId')?.value;
    const to = fg.get('warehouseToId')?.value;
    return from && to && from === to ? { sameWarehouse: true } : null;
  };

  // Deshabilita en "Destino" la opción del almacén ya elegido en "Origen"
  // (se pasa a app-generic-select vía [optionDisabled]).
  isSameAsOrigin = (option: any): boolean => {
    const from = this.fgRefer?.get('warehouseFromId')?.value;
    return !!from && from === option?.id;
  };

  createProduct(product: ProductInvoiceDto): FormGroup {
    return this.fb.group({
      colorId: [product.colorId],
      // Sin el modal de "agregar cantidad": el producto entra con 1 y la
      // cantidad se ajusta después con el stepper de la lista.
      count: [product.quantity || 1, [Validators.required, Validators.min(1)]],
      discount: [0],
      iva: [0],
      name: [product.name],
      code: [product.code],
      stock: [product.stock],
      price: [product.price],
      productId: [product.id],
      productName: [product.name],
      sizeId: [product.sizeId],
      variantId: [product.variantId]
    })
  }

  // Stepper de cantidad por fila (mínimo 1).
  incQty(index: number): void {
    const control = this.productsArr.at(index).get('count');
    control.setValue((Number(control.value) || 0) + 1);
  }

  decQty(index: number): void {
    const control = this.productsArr.at(index).get('count');
    const next = (Number(control.value) || 0) - 1;
    control.setValue(next < 1 ? 1 : next);
  }

  // Aviso no bloqueante: la cantidad supera el stock disponible de la variante.
  isOverStock(item: any): boolean {
    return Number(item.value.count) > Number(item.value.stock);
  }

  deleteProduct(productIndex: number) {
    this.productsArr.removeAt(productIndex);
  }

  getTotal(): number {
    let total = this.productsArr.value.reduce((sum, current) => sum + current.count, 0);
    return total;
  }

  save() {
    // -- Save loading
    this.saving = true;
    this._invoiceService.createDeliveryNote(this.fgRefer.value as DeliveryNoteDto).subscribe(
      () => {
        this.notify.success(this.l('SavedSuccessfully'), this.l('Close'));
        this.dialogRef.close(true);
      }, () => {
        this.saving = false;
      }
    );
  }

  onNoClick(): void {
    abp.message.confirm(
      this.l('InformationWillBeLost'),    //traducir
      undefined,
      (result: boolean) => {
        if (result) {
          this.dialogRef.close();
        }
      }
    );
  }

  searchProductsFromComponent(data: any) {
    const a = this.productsArr.value.findIndex(
      (item) =>
        item.productId === data.id && item.variantId === data.variantId
    );
    if (a != -1) {
      let productCount = this.productsArr.at(a).get('count').value as number;
      this.productsArr.at(a).get('count').setValue(productCount + (data.quantity || 1));
      return;
    }

    // El resultado del buscador no trae el stock real de la variante (antes
    // lo resolvía el modal de "agregar cantidad" que ya no se usa). Se pide
    // acá para que el aviso de sobre-stock compare contra el valor correcto.
    const id = data.isVariant ? data.variantId : data.id;
    this._invoiceService.getProductWithVariant(id, data.isVariant, undefined, undefined).subscribe({
      next: (full) => {
        const product = this.createProduct({ ...data, stock: full.stock });
        this.productsArr.push(product);
      },
      error: () => {
        // Si falla el detalle, se agrega igual con el stock que haya en data.
        this.productsArr.push(this.createProduct(data));
      }
    });
  }
}
